// Define global variables
let crfData = [];
let CSLstandardIDs = [];
let crfMappingValues = [];
let crfMappingToFriendlyName = {};
let dataCache = {
    CRF: {},
    CP: {},
    CSL: {},
    CS: {}
};
let groupedStandardCounts = [];
let groupedCRFcounts = [];
let scorecardResultsCache = {};
/** Scorecard tool is fixed to CRF / safeguards year 2026. */
const SCORECARD_FRAMEWORK_VERSION = '2026';
const scorecardPayloadCache = {};
var charts = {};

/** Letter grades in the overview table must never use this selector — only detail UI. */
const LETTER_GRADE_DETAIL_SELECTOR = '#scorecardDetailSection .letter-grade';
const SCORECARD_DETAIL_ROOT = '#scorecardDetailSection';

function getScorecardDetailModal() {
    return document.getElementById('scorecardDetailModal');
}

function showScorecardDetailModal() {
    const el = getScorecardDetailModal();
    if (!el) {
        return;
    }
    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        bootstrap.Modal.getOrCreateInstance(el).show();
    } else {
        el.classList.add('show');
        el.style.display = 'block';
        document.body.classList.add('modal-open');
    }
}

function hideScorecardDetailModal() {
    const el = getScorecardDetailModal();
    if (!el) {
        return;
    }
    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const inst = bootstrap.Modal.getInstance(el);
        if (inst) {
            inst.hide();
        }
    } else {
        el.classList.remove('show');
        el.style.display = 'none';
        document.body.classList.remove('modal-open');
    }
}

function forEachDetailLetterGrade(callback) {
    document.querySelectorAll(LETTER_GRADE_DETAIL_SELECTOR).forEach(callback);
}

function getScorecardFrameworkVersion() {
    return SCORECARD_FRAMEWORK_VERSION;
}

function setScorecardDetailLoaderVisible(visible) {
    const el = document.getElementById('scorecard-detail-loader');
    if (!el) {
        return;
    }
    el.style.display = visible ? 'flex' : 'none';
}

function getScorecardChartWrap() {
    const canvas = document.getElementById('chartCanvas');
    return canvas ? canvas.closest('.scorecard-chart-canvas-wrap') : null;
}

/** Toggle the modal safeguard chart + its sizing wrapper together. */
function setScorecardChartAreaVisible(visible) {
    const canvas = document.getElementById('chartCanvas');
    const wrap = getScorecardChartWrap();
    const disp = visible ? 'block' : 'none';
    if (canvas) {
        canvas.style.display = disp;
    }
    if (wrap) {
        wrap.style.display = disp;
    }
}

/** Same pattern as risk / asset register: overlay until overview AJAX finishes. */
function setScorecardOverviewLoading(isLoading) {
    const view = document.getElementById('scorecard-overview-view');
    const loader = document.getElementById('scorecard-overview-loader');
    if (!view || !loader) {
        return;
    }
    view.classList.toggle('scorecard-overview-loading', !!isLoading);
    if (isLoading) {
        loader.classList.remove('d-none');
    } else {
        loader.classList.add('d-none');
    }
}

function getGradeClass(letterGrade) {
    switch (letterGrade) {
        case 'A+':
        case 'A':
        case 'A-':
            return 'grade-a';
        case 'B+':
        case 'B':
        case 'B-':
            return 'grade-b';
        case 'C+':
        case 'C':
        case 'C-':
            return 'grade-c';
        case 'D+':
        case 'D':
        case 'D-':
            return 'grade-d';
        default:
            return 'grade-f';
    }
}

/** Numeric sort key for overview letter grades (best = highest). Unknown / N/A last. */
function scorecardOverviewGradeRank(letterGrade) {
    const g = String(letterGrade || '').trim().toUpperCase();
    const order = {
        'A+': 17,
        A: 16,
        'A-': 15,
        'B+': 14,
        B: 13,
        'B-': 12,
        'C+': 11,
        C: 10,
        'C-': 9,
        'D+': 8,
        D: 7,
        'D-': 6,
        F: 5
    };
    return order[g] ?? 0;
}

function sortScorecardOverviewRows(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return [];
    }

    return rows.slice().sort((a, b) => {
        const scoreA = parseInt(a.overall_score ?? a.OVERALL_SCORE ?? 0, 10);
        const scoreB = parseInt(b.overall_score ?? b.OVERALL_SCORE ?? 0, 10);
        if (scoreB !== scoreA) {
            return scoreB - scoreA;
        }
        const rankA = scorecardOverviewGradeRank(a.letter_grade || a.LETTER_GRADE);
        const rankB = scorecardOverviewGradeRank(b.letter_grade || b.LETTER_GRADE);
        if (rankB !== rankA) {
            return rankB - rankA;
        }
        const na = (a.friendly_name || a.FRIENDLY_NAME || '').toLowerCase();
        const nb = (b.friendly_name || b.FRIENDLY_NAME || '').toLowerCase();
        return na.localeCompare(nb);
    });
}

async function loadScorecardOverview(frameworkVersion = SCORECARD_FRAMEWORK_VERSION) {
    const overviewBody = document.querySelector('#scorecardOverview tbody');
    const emptyState = document.getElementById('scorecardOverviewEmpty');
    if (!overviewBody) {
        return;
    }

    setScorecardOverviewLoading(true);
    overviewBody.innerHTML = '';

    try {
        const response = await jQuery.ajax({
            type: 'POST',
            url: myAjax.ajaxurl,
            data: {
                action: 'get_scorecard_overview',
                framework_version: frameworkVersion
            },
            dataType: 'json'
        });

        if (!response || response.success !== true) {
            const msg = (response && response.data) ? response.data : 'Failed to load scorecard overview';
            throw new Error(typeof msg === 'string' ? msg : 'Failed to load scorecard overview');
        }

        const rows = Array.isArray(response.data && response.data.rows) ? response.data.rows : [];
        renderScorecardOverview(rows, emptyState);
    } catch (error) {
        console.error('Failed to load scorecard overview:', error);
        if (emptyState) {
            emptyState.classList.remove('d-none');
        }
    } finally {
        setScorecardOverviewLoading(false);
    }
}

function renderScorecardOverview(rows, emptyState) {
    const overviewBody = document.querySelector('#scorecardOverview tbody');
    if (!overviewBody) {
        return;
    }

    overviewBody.innerHTML = '';

    if (!rows.length) {
        if (emptyState) {
            emptyState.classList.remove('d-none');
        }
        return;
    }

    if (emptyState) {
        emptyState.classList.add('d-none');
    }

    try {
        sortScorecardOverviewRows(rows).forEach((row) => {
            const standardId = row.standard_id || row.STANDARD_ID || '';
            const friendlyKey = row.friendly_name || row.FRIENDLY_NAME || standardId;
            const standardName = (row.standard_name || row.STANDARD_NAME || '').trim();
            const typeText = (row.standard_type || row.STANDARD_TYPE || '').trim();
            const versionText = (row.standard_version || row.STANDARD_VERSION || '').trim();
            if (!standardId) {
                return;
            }

            const tr = document.createElement('tr');
            tr.dataset.standardId = standardId;

            const tdStandardName = document.createElement('td');
            tdStandardName.className = 'scorecard-col-standard-name';
            tdStandardName.textContent = standardName || String(friendlyKey || '').toUpperCase();

            const tdType = document.createElement('td');
            tdType.className = 'scorecard-col-standard-type';
            tdType.textContent = typeText;

            const tdVersion = document.createElement('td');
            tdVersion.className = 'scorecard-col-version';
            tdVersion.textContent = versionText;

            const tdGrade = document.createElement('td');
            tdGrade.className = 'text-nowrap scorecard-overview-grade scorecard-col-grade';
            const gradeSpan = document.createElement('span');
            gradeSpan.className = `letter-grade ${getGradeClass(row.letter_grade || row.LETTER_GRADE)}`;
            gradeSpan.textContent = row.letter_grade || row.LETTER_GRADE || 'N/A';
            tdGrade.appendChild(gradeSpan);

            const tdDetails = document.createElement('td');
            tdDetails.className = 'scorecard-col-details';
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'btn btn-sm btn-outline-primary scorecard-view-detail';
            btn.dataset.standardId = standardId;
            btn.setAttribute('aria-label', 'View scorecard details');
            btn.innerHTML = '<span class="visually-hidden">Details</span><i class="fas fa-eye" aria-hidden="true"></i>';
            tdDetails.appendChild(btn);

            const friendlyDisplay = String(friendlyKey || '').trim().toUpperCase();

            tr.appendChild(tdStandardName);
            tr.appendChild(tdType);
            tr.appendChild(tdVersion);
            tr.appendChild(tdGrade);
            tr.appendChild(tdDetails);

            btn.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                openScorecardDetail(standardId, friendlyDisplay);
            });

            overviewBody.appendChild(tr);
        });
    } catch (err) {
        console.error('Scorecard overview render failed:', err);
        if (emptyState) {
            emptyState.classList.remove('d-none');
        }
    }
}

async function openScorecardDetail(standardId, friendlyName = '') {
    const displayTitle = (friendlyName && String(friendlyName).trim())
        ? String(friendlyName).trim()
        : String(standardId || '');

    const heading = document.querySelector(`${SCORECARD_DETAIL_ROOT} .current-standard h1`);
    if (heading) {
        heading.textContent = displayTitle;
    }

    showScorecardDetailModal();

    const friendlyFilter = document.getElementById('friendlyname-filter');
    if (friendlyFilter) {
        friendlyFilter.value = standardId;
    }

    const cacheKey = getStandardCacheKey(standardId);
    if (scorecardPayloadCache[cacheKey]) {
        applyScorecardPayload(scorecardPayloadCache[cacheKey]);
        return;
    }

    try {
        const response = await jQuery.ajax({
            type: 'POST',
            url: myAjax.ajaxurl,
            data: {
                action: 'get_scorecard_detail',
                framework_version: getScorecardFrameworkVersion(),
                standard_id: standardId
            },
            dataType: 'json'
        });

        if (response.success) {
            const payload = response.data.score_payload || {};
            scorecardPayloadCache[cacheKey] = payload;
            applyScorecardPayload(payload);
            return;
        }
    } catch (error) {
        console.warn('Cached scorecard detail unavailable, falling back to live computation.', error);
    }

    await filterStandardsData(standardId);
    fetchStandardData(standardId, window.scorecardCslData || []);
}

function ensureScorecardDetailTablesVisible() {
    const safeguardTable = document.getElementById('scorecardTable');
    if (safeguardTable) {
        safeguardTable.classList.remove('d-none');
    }
}

function applyScorecardPayload(payload) {
    if (!payload || !payload.standard_id) {
        return;
    }

    if (payload.friendly_name) {
        const titleEl = document.querySelector(`${SCORECARD_DETAIL_ROOT} .current-standard h1`);
        if (titleEl) {
            titleEl.textContent = String(payload.friendly_name).trim().toUpperCase();
        }
    }

    if (payload.grouped_crf_counts) {
        groupedCRFcounts = payload.grouped_crf_counts;
    }
    if (payload.grouped_standard_counts) {
        groupedStandardCounts = payload.grouped_standard_counts;
        scorecardResultsCache[getStandardCacheKey(payload.standard_id)] = groupedStandardCounts;
    }

    Object.keys(payload.safeguard_prefix_scores || {}).forEach((prefix) => {
        const scorecardCell = document.querySelector(`.crf-${prefix}`);
        if (scorecardCell) {
            scorecardCell.textContent = payload.safeguard_prefix_scores[prefix];
        }
    });

    const overallCell = document.querySelector('.crf-overall');
    if (overallCell && payload.safeguard_overall) {
        overallCell.textContent = payload.safeguard_overall;
    }

    const scopeRaw = payload.scope_raw || {};
    Object.keys(scopeRaw).forEach((className) => {
        const element = document.querySelector(`.${className}`);
        if (!element) {
            return;
        }

        element.innerText = scopeRaw[className];
        const score = (payload.scope_scores || {})[className];
        element.setAttribute('data-score', score ? `${score}%` : '0%');
        element.style.backgroundColor = getBackgroundColor(score);
    });

  const addressedClasses = [
        'addressed-gov',
        'addressed-op',
        'addressed-id',
        'addressed-comp',
        'addressed-net',
        'addressed-cloud',
        'addressed-sd'
    ];
    addressedClasses.forEach((className) => {
        const element = document.querySelector(`.${className}`);
        const score = (payload.scope_scores || {})[className];
        if (element && typeof score !== 'undefined') {
            element.innerText = `${score}%`;
            element.style.backgroundColor = getBackgroundColor(score);
        }
    });

    const overallScore = payload.overall_score ?? 0;
    const scopeOverall = document.querySelector('.scope-overall');
    if (scopeOverall) {
        scopeOverall.innerText = `${overallScore}%`;
    }

    assignLetterGrade(overallScore);
    createChartFromTable('scorecardTable', 'chartCanvas', charts, true);
    ensureScorecardDetailTablesVisible();
    document.getElementById('scorecardScope').style.display = 'table';

    requestAnimationFrame(() => {
        if (charts.chartCanvas && typeof charts.chartCanvas.resize === 'function') {
            charts.chartCanvas.resize();
        }
    });
}

function buildScorecardPayloadFromCurrentState(standardId, friendlyName) {
    const safeguardPrefixScores = {};
    document.querySelectorAll('#scorecardTable tbody td[class^="crf-"]').forEach((cell) => {
        const className = Array.from(cell.classList).find((name) => name.indexOf('crf-') === 0);
        if (!className || className === 'crf-overall') {
            return;
        }

        safeguardPrefixScores[className.replace('crf-', '')] = cell.textContent.trim();
    });

    const scopeScores = {};
    const scopeRaw = {};
    document.querySelectorAll('#scorecardScope tbody td:nth-child(2)').forEach((cell) => {
        const className = Array.from(cell.classList).find((name) => name !== '');
        if (!className) {
            return;
        }

        const score = cell.getAttribute('data-score')
            ? cell.getAttribute('data-score').replace('%', '').trim()
            : cell.innerText.replace('%', '').trim();
        scopeScores[className] = score;
        if (cell.getAttribute('data-score')) {
            scopeRaw[className] = cell.innerText.trim();
        }
    });

    const overallScore = parseInt(document.querySelector('.scope-overall')?.innerText.replace('%', '') || '0', 10);
    const letterGrade = computeLetterGradeFromPercent(overallScore);
    const meta = getCslMetaForStandard(standardId);

    return {
        framework_version: getScorecardFrameworkVersion(),
        standard_id: getStandardCacheKey(standardId),
        friendly_name: friendlyName,
        standard_name: meta.standard_name,
        standard_type: meta.standard_type,
        standard_version: meta.standard_version,
        letter_grade: letterGrade,
        overall_score: overallScore,
        safeguard_overall: document.querySelector('.crf-overall')?.textContent.trim() || '0.00',
        grouped_standard_counts: groupedStandardCounts,
        grouped_crf_counts: groupedCRFcounts,
        safeguard_prefix_scores: safeguardPrefixScores,
        scope_scores: scopeScores,
        scope_raw: scopeRaw
    };
}

async function persistScorecardPayload(payload) {
    if (!payload || !payload.standard_id) {
        return;
    }

    scorecardPayloadCache[getStandardCacheKey(payload.standard_id)] = payload;

    try {
        await jQuery.ajax({
            type: 'POST',
            url: myAjax.ajaxurl,
            data: {
                action: 'save_scorecard_score',
                score_payload: JSON.stringify(payload)
            },
            dataType: 'json'
        });
        await loadScorecardOverview(SCORECARD_FRAMEWORK_VERSION);
    } catch (error) {
        console.warn('Failed to persist scorecard payload:', error);
    }
}

function getStandardCacheKey(CSLstandardID) {
    const id = Array.isArray(CSLstandardID) ? CSLstandardID[0] : CSLstandardID;
    return String(id).toUpperCase();
}

/** CSL list item fields for overview columns and persistence (cybersecurity-standards-list). */
function getCslMetaForStandard(standardId) {
    const csl = window.scorecardCslData || [];
    const key = getStandardCacheKey(standardId);
    const item = csl.find((i) => i.standardid?.S && getStandardCacheKey(i.standardid.S) === key);
    if (!item) {
        return { standard_name: '', standard_type: '', standard_version: '' };
    }
    return {
        standard_name: item.standardname?.S || '',
        standard_type: item.standardtype?.S || '',
        standard_version: item.standardversion?.S || ''
    };
}

function createChartFromTable(tableId, chartId, charts, isHorizontal = true) {
    const table = document.getElementById(tableId);
    if (!table) {
        console.error('Table not found:', tableId);
        return;
    }

    const rows = table.getElementsByTagName('tr');
    const labels = [];
    const policyData = [];

    for (let i = 1; i < rows.length - 1; i++) {
        const cells = rows[i].getElementsByTagName('td');
        if (cells.length < 2) {
            console.warn(`Skipped row ${i} in table ${tableId} due to insufficient cells.`);
            continue;
        }
        labels.push(cells[0].innerText);
        const score = parseFloat(cells[1].innerText);
        policyData.push((score * 100).toFixed(0));
    }

    const canvas = document.getElementById(chartId);
    if (!canvas) {
        console.error('Canvas not found:', chartId);
        return;
    }

    const wrap = canvas.closest('.scorecard-chart-canvas-wrap');

    if (charts[chartId]) {
        charts[chartId].destroy();
        charts[chartId] = null;
    }

    canvas.removeAttribute('width');
    canvas.removeAttribute('height');
    canvas.style.width = '';
    canvas.style.height = '';

    // Horizontal sizing + tick/grid spacing: match WebAssessmentCharts.createChartFromTable
    // (js/assessments/controls/createChartFromTable.js — dashboard "Maturity Ratings By Safeguard Domain" chart).
    const MATURITY_RATINGS_BAND_PX = 50;
    const MATURITY_RATINGS_MIN_HEIGHT = 200;
    const MATURITY_RATINGS_MAX_HEIGHT = 1000;

    const shouldSizeHorizontalBars = isHorizontal && labels.length > 0;
    const useFixedHorizontalLayout = shouldSizeHorizontalBars && !!wrap;

    if (useFixedHorizontalLayout) {
        const calculatedHeight = Math.min(
            MATURITY_RATINGS_MAX_HEIGHT,
            Math.max(MATURITY_RATINGS_MIN_HEIGHT, labels.length * MATURITY_RATINGS_BAND_PX)
        );
        wrap.style.height = `${calculatedHeight}px`;
    } else if (wrap) {
        wrap.style.height = '';
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        console.error('Canvas context not found:', chartId);
        return;
    }

    const datasetBase = {
        label: 'Maturity Score (%)',
        data: policyData,
        backgroundColor: 'rgba(49, 77, 118, 1)',
        hoverBackgroundColor: 'rgba(49, 77, 118, 1)',
        borderColor: 'rgb(49, 77, 118)',
        borderWidth: 1
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: !useFixedHorizontalLayout,
        indexAxis: isHorizontal ? 'y' : 'x',
        scales: {
            x: {
                beginAtZero: true,
                max: 100,
                ticks: {
                    callback: function(value) { return value + '%'; },
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                    padding: 5
                },
                grid: {
                    offset: true
                }
            },
            y: {
                beginAtZero: true,
                offset: true,
                ticks: {
                    autoSkip: false,
                    padding: 5
                },
                grid: {
                    offset: true
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function(tooltipItem) {
                        return tooltipItem.raw + '%';
                    }
                }
            }
        }
    };

    charts[chartId] = new Chart(ctx, {
        type: 'bar',
        data: {
            labels,
            datasets: [datasetBase]
        },
        options: chartOptions
    });
}



async function fetchData(year = "", CSLstandardID = [], initialize = false) {
    year = SCORECARD_FRAMEWORK_VERSION;
	setScorecardDetailLoaderVisible(true);
	document.getElementById('scorecardScope').style.display = 'none';
	
    let apisToQuery = [];

    // Determine which APIs need to be queried
    if (!dataCache.CRF[year]) apisToQuery.push('CRF');
    if (!dataCache.CSL[year]) apisToQuery.push('CSL');
    if (!dataCache.CS[getStandardCacheKey(CSLstandardID)]) apisToQuery.push('CS');

    // Perform AJAX call only if there are APIs to query
    if (apisToQuery.length > 0) {
        try {
            const response = await jQuery.ajax({
                type: 'POST',
                url: myAjax.ajaxurl,
                data: {
                    action: 'api_action',
                    year: year,
                    CSLstandardID: CSLstandardID,
                    apis: apisToQuery
                },
                dataType: 'json'
            });

            console.log('response', response);
            if (response.error) {
                throw new Error(response.error);
            }

            // Update cache with new data
            if (response.CRF) dataCache.CRF[year] = response.CRF;
            if (response.CP) dataCache.CP[year] = response.CP;
            if (response.CSL) dataCache.CSL[year] = response.CSL;
            if (response.CS) dataCache.CS[getStandardCacheKey(CSLstandardID)] = response.CS;

            // After data has been cached, extract unique CRF safeguards
            const CRFsafeguards = extractUniqueCRFSafeguards(year);
            console.log('Extracted CRF safeguards:', CRFsafeguards);
            
            // Group CRF safeguard counts after extraction
            groupedCRFcounts = groupCRFandCountByMapping(CRFsafeguards);
            console.log('Grouped CRF counts:', groupedCRFcounts);
			
			

        } catch (error) {
            console.error("AJAX Error:", error);
            if (typeof dataContainer !== 'undefined' && dataContainer) {
                dataContainer.innerHTML = `<p class="error-message">Error fetching data: ${error.message}</p>`;
            }
            if (initialize) {
                window.scorecardCslData = window.scorecardCslData || [];
                void loadScorecardOverview(SCORECARD_FRAMEWORK_VERSION);
            }
            return;
        }
    }

    // Continue to process with cached or newly fetched data
    let parsedDataCRF = dataCache.CRF[year];
    let parsedDataCP = dataCache.CP[year];
    let parsedDataCSL = dataCache.CSL[year];
    let parsedDataCS = dataCache.CS[getStandardCacheKey(CSLstandardID)];

    // Assuming Items is an array in your data structure
    const crfData = parsedDataCRF ? parsedDataCRF.Items : [];
    const cslData = parsedDataCSL ? parsedDataCSL.Items : [];
    const csItems = parsedDataCS && Array.isArray(parsedDataCS.Items) ? parsedDataCS.Items : [];
    const crfMappingValues = csItems.flatMap(item =>
        item.crfmapping && item.crfmapping.S
            ? item.crfmapping.S.split('\n').map(value => value.trim())
            : []
    );
    const crfMappingToFriendlyName = csItems.reduce((acc, item) => {
        if (!item.crfmapping || !item.crfmapping.S || !item.friendlyname || !item.friendlyname.S) {
            return acc;
        }
        const mappings = item.crfmapping.S.split('\n').map(str => str.trim());
        mappings.forEach(mapping => {
            acc[mapping] = item.friendlyname.S;
        });
        return acc;
    }, {});

    if (initialize) {
        window.scorecardCslData = cslData;
        setInitialDropdownValues(year, CSLstandardID, cslData);
        void loadScorecardOverview(year);
    }
}

function fetchStandardData(CSLstandardID, cslData) {
	setScorecardChartAreaVisible(false);
	document.getElementById('scorecardScope').style.display = 'none';
	forEachDetailLetterGrade((el) => {
		el.style.display = 'none';
	});
	const detailHeading = document.querySelector(`${SCORECARD_DETAIL_ROOT} .current-standard h1`);
	if (detailHeading) {
		detailHeading.style.display = 'none';
	}
	setScorecardDetailLoaderVisible(true);
    if (cslData && Array.isArray(cslData)) {
        // Find the item for the selected CSLstandardID
        const item = cslData.find(item => item.standardid.S === CSLstandardID);
        console.log('item:', item);
        if (item) {
            // Define the fields and corresponding class names to update
            const fields = {
                uptodate: '.updated-recently',
                opendev: '.community',
                popularity: '.popularity',
                mapthreats: '.threats-mapped',
                modernthreats: '.modern',
                mappings: '.other-mapped',
                tagged: '.applicability',
                prioritized: '.prioritizes',
                measures: '.guide'
            };

            // Define a mapping of values to scores
            const scoreMapping = {
                'Yes': '100%',
                'No': '0%',
                'Partial': '50%',
                'High': '100%',
                'Moderate': '66%',
                'Low': '33%',
                'N/A': 'N/A'
            };

            // Helper function to determine background color based on score
            function getBackgroundColor(score) {
                const numericScore = parseFloat(score);
                if (numericScore <= 33) {
                    return '#ff000029'; // Red
                } else if (numericScore <= 66) {
                    return '#ffbc0029'; // Yellow
                } else if (numericScore <= 100) {
                    return '#00ff4329'; // Green
                }
                return ''; // No color for N/A or invalid scores
            }

            // Loop through each field and update the corresponding element
            Object.keys(fields).forEach(field => {
                const value = item[field]?.S || 'N/A';  // Get the value from cslData
                console.log(`Processing field: ${field}, Value: ${value}`); // Log the field and its value
                if (!item[field]?.S) {
                    console.log(`Field ${field} is marked as N/A because it is missing or undefined in the item.`);
                }
                const score = scoreMapping[value] || 'N/A';  // Map the value to the score
                console.log(`Mapped score for field ${field}: ${score}`); // Log the mapped score
                if (!scoreMapping[value]) {
                    console.log(`Value ${value} for field ${field} is marked as N/A because it is not in the scoreMapping.`);
                }

                const element = document.querySelector(fields[field]);
                if (element) {
                    element.innerHTML = value;  // Keep the original value as the displayed text
                    element.setAttribute('data-score', score);  // Set the score as a data attribute

                    // Set background color based on score
                    const backgroundColor = getBackgroundColor(score);
                    element.style.backgroundColor = backgroundColor;
                    console.log(`Updated element for field ${field} with value: ${value}, score: ${score}, backgroundColor: ${backgroundColor}`); // Log the element update
                }
            });
        } else {
            console.warn(`No matching item found for ${CSLstandardID}`);
        }
    } else {
        console.warn('No CSL data available to fetch values');
    }
}

function calculateAveragesAndUpdateTable() {
    // Helper function to get the score from the cell (either from text or data attribute)
    function getScore(cell) {
        let valueText = cell.innerText.trim(); // Trim the text

        // Check if the value is a percentage
        if (valueText.includes('%')) {
            valueText = valueText.replace('%', '').trim(); // Remove % symbol and trim
        } else {
            // Check if the cell has a data-score attribute and use that if present
            valueText = cell.getAttribute('data-score') ? cell.getAttribute('data-score').replace('%', '').trim() : '0';
        }

        return parseFloat(valueText); // Convert to float
    }

    // Calculate averages for addressed safeguards
    function calculateAverage(selectors) {
        let sum = 0;
        let count = 0;
        console.log('Calculating average for selectors:', selectors); // Log the selectors being processed
        selectors.forEach(selector => {
            const element = document.querySelector(`.${selector}`);
            if (element) {
                const value = parseFloat(element.innerText.replace('%', '')) || 0; // Convert % to number
                console.log(`Selector: .${selector}, Value: ${value}`); // Log the selector and its value
                sum += value;
                count++;
            } else {
                console.warn(`Element not found for selector: .${selector}`); // Warn if the element is not found
            }
        });
        const average = count > 0 ? (sum / count).toFixed(2) : '0.00';  // Return average as decimal
        console.log(`Sum: ${sum}, Count: ${count}, Average: ${average}`); // Log the sum, count, and calculated average
        return average;
    }

    // Define categories with their corresponding selectors
    const categories = {
        "governance": ['crf-prg', 'crf-saf', 'crf-edu', 'crf-ast', 'crf-imp', 'crf-val', 'crf-tpr', 'crf-com'],
        "operational": ['crf-res', 'crf-phy', 'crf-prv', 'crf-ai'],
        "identity": ['crf-id', 'crf-pam', 'crf-dta', 'crf-am', 'crf-log'],
        "computing": ['crf-sys', 'crf-sw', 'crf-cfg', 'crf-vul', 'crf-mdm'],
        "network": ['crf-ndm', 'crf-pna', 'crf-ina'],
        "cloud": ['crf-csp', 'crf-em'],
        "software": ['crf-sdm', 'crf-sdv', 'crf-sdo']
    };

    // Calculate averages for each category
    const averages = {
        governance: calculateAverage(categories.governance),
        operational: calculateAverage(categories.operational),
        identity: calculateAverage(categories.identity),
        computing: calculateAverage(categories.computing),
        network: calculateAverage(categories.network),
        cloud: calculateAverage(categories.cloud),
        software: calculateAverage(categories.software)
    };

    // Convert the decimal averages into percentages and update the table
    document.querySelector('.addressed-gov').innerText = (averages.governance * 100).toFixed(0) + '%';
    document.querySelector('.addressed-op').innerText = (averages.operational * 100).toFixed(0) + '%';
    document.querySelector('.addressed-id').innerText = (averages.identity * 100).toFixed(0) + '%';
    document.querySelector('.addressed-comp').innerText = (averages.computing * 100).toFixed(0) + '%';
    document.querySelector('.addressed-net').innerText = (averages.network * 100).toFixed(0) + '%';
    document.querySelector('.addressed-cloud').innerText = (averages.cloud * 100).toFixed(0) + '%';
    document.querySelector('.addressed-sd').innerText = (averages.software * 100).toFixed(0) + '%';

    // Calculate and update the overall score as a percentage
    const scoreCells = document.querySelectorAll('#scorecardScope tbody td:nth-child(2)'); // Select all second column cells in the tbody
    let totalScore = 0;
    let count = 0;

    // Define the emphasized classes
    const emphasizedClasses = ['addressed-comp', 'addressed-cloud', 'updated-recently', 'modern', 'prioritizes'];

    scoreCells.forEach(cell => {
        const score = getScore(cell); // Get the score from the cell

        if (!isNaN(score)) { // Ensure it's a valid number
            // Check if the cell has any of the emphasized classes
            const cellClasses = cell.classList;
            let isEmphasized = false;
            for (let cls of emphasizedClasses) {
                if (cellClasses.contains(cls)) {
                    isEmphasized = true;
                    break;
                }
            }

            if (isEmphasized) {
                totalScore += score * 2; // Double the value
                count += 2; // Increase count by 2
            } else {
                totalScore += score;
                count++;
            }

            // Apply background color based on score
            let backgroundColor = getBackgroundColor(score);
            cell.style.backgroundColor = backgroundColor;
        }
    });

    const overallScore = count > 0 ? (totalScore / count).toFixed(0) : '0'; // Calculate the average

    document.querySelector('.scope-overall').innerText = overallScore + '%';

    // Assign and display the letter grade based on the overall score
    const letterGrade = assignLetterGrade(overallScore);

    console.log('Overall Score:', overallScore + '%');
    console.log('Letter Grade:', letterGrade);

    document.getElementById('scorecardScope').style.display = '';
}

// Helper function to determine background color based on score
function getBackgroundColor(score) {
    const numericScore = parseFloat(score);
    if (numericScore <= 33) {
        return '#ff000029'; // Red
    } else if (numericScore <= 66) {
        return '#ffbc0029'; // Yellow
    } else if (numericScore <= 100) {
        return '#00ff4329'; // Green
    }
    return ''; // No color for N/A or invalid scores
}

/**
 * Map overall score (0–100) to a letter grade. No DOM side effects.
 * @param {number|string} overallScore
 * @returns {string}
 */
function computeLetterGradeFromPercent(overallScore) {
    const numericScore = parseFloat(overallScore);

    if (isNaN(numericScore)) {
        return 'N/A';
    }

    if (numericScore >= 95) {
        return 'A+';
    }
    if (numericScore >= 88) {
        return 'A';
    }
    if (numericScore >= 80) {
        return 'A-';
    }
    if (numericScore >= 73) {
        return 'B+';
    }
    if (numericScore >= 66) {
        return 'B';
    }
    if (numericScore >= 60) {
        return 'B-';
    }
    if (numericScore >= 53) {
        return 'C+';
    }
    if (numericScore >= 46) {
        return 'C';
    }
    if (numericScore >= 40) {
        return 'C-';
    }
    if (numericScore >= 31) {
        return 'D+';
    }
    if (numericScore >= 23) {
        return 'D';
    }
    if (numericScore >= 15) {
        return 'D-';
    }

    return 'F';
}

function assignLetterGrade(overallScore) {
    const letterGrade = computeLetterGradeFromPercent(overallScore);

    function setGradeBackground(gradeElement, grade) {
        const gradeBuckets = ['grade-a', 'grade-b', 'grade-c', 'grade-d', 'grade-f'];
        let gradeClass = 'grade-f';
        switch (grade) {
            case 'A+':
            case 'A':
            case 'A-':
                gradeClass = 'grade-a';
                break;
            case 'B+':
            case 'B':
            case 'B-':
                gradeClass = 'grade-b';
                break;
            case 'C+':
            case 'C':
            case 'C-':
                gradeClass = 'grade-c';
                break;
            case 'D+':
            case 'D':
            case 'D-':
                gradeClass = 'grade-d';
                break;
            default:
                gradeClass = 'grade-f';
        }
        gradeBuckets.forEach((c) => gradeElement.classList.remove(c));
        gradeElement.classList.add(gradeClass);
    }

    forEachDetailLetterGrade((gradeElement) => {
        gradeElement.textContent = letterGrade;
        setGradeBackground(gradeElement, letterGrade);
    });

    // Update the display properties for the chart, scorecard, and loader
    setScorecardChartAreaVisible(true);
    document.getElementById('scorecardScope').style.display = 'table';
    forEachDetailLetterGrade((gradeElement) => {
        gradeElement.style.display = 'inline-block';
    });
    document.querySelector(`${SCORECARD_DETAIL_ROOT} .current-standard h1`).style.display = 'inline-block';
    setScorecardDetailLoaderVisible(false);

    return letterGrade;
}



function setInitialDropdownValues(year, CSLstandardID, cslData) {
    // Populate CSL standard filter options
    const friendlyFilterSelect = document.getElementById("friendlyname-filter");
    const friendlyArray = cslData.map(item => {
        return {
            friendlyName: item.friendlyname?.S?.toLowerCase(),
            standardId: item.standardid?.S,
            comingSoon: item.comingsoon?.S?.toLowerCase()
        };
    }).filter(data => data.friendlyName && data.standardId && data.comingSoon === 'no');

    friendlyFilterSelect.innerHTML = "<option value='all' selected='selected' disabled='disabled'>Select</option>";
    // CRF-S: Core (v2021..v2026) first, then 2026 sub-versions (AppSec, Gov, Hyg, SMB) after Core (v2026)
    const crfsSubOrder = { core: 0, appsec: 1, gov: 2, hyg: 3, smb: 4 };
    friendlyArray.sort((a, b) => {
        const na = (a.friendlyName || '').toLowerCase();
        const nb = (b.friendlyName || '').toLowerCase();
        const isCrfsA = na.indexOf('crf-s') === 0;
        const isCrfsB = nb.indexOf('crf-s') === 0;
        if (!isCrfsA && !isCrfsB) return (a.friendlyName || '').localeCompare(b.friendlyName || '');
        if (!isCrfsA) return 1;
        if (!isCrfsB) return -1;
        const ma = (na.match(/\(v(\d{4})\)/) || [])[1];
        const mb = (nb.match(/\(v(\d{4})\)/) || [])[1];
        const yearA = ma ? parseInt(ma, 10) : 0;
        const yearB = mb ? parseInt(mb, 10) : 0;
        if (yearA !== yearB) return yearA - yearB;
        const coreA = na.indexOf('core') !== -1 ? 0 : 1;
        const coreB = nb.indexOf('core') !== -1 ? 0 : 1;
        if (yearA === 2026 && coreA && coreB) {
            let keyA = 'core';
            let keyB = 'core';
            if (na.indexOf('appsec') !== -1) keyA = 'appsec';
            else if (na.indexOf('gov') !== -1) keyA = 'gov';
            else if (na.indexOf('hyg') !== -1) keyA = 'hyg';
            else if (na.indexOf('smb') !== -1 || na.indexOf('small business') !== -1) keyA = 'smb';
            if (nb.indexOf('appsec') !== -1) keyB = 'appsec';
            else if (nb.indexOf('gov') !== -1) keyB = 'gov';
            else if (nb.indexOf('hyg') !== -1) keyB = 'hyg';
            else if (nb.indexOf('smb') !== -1 || nb.indexOf('small business') !== -1) keyB = 'smb';
            return (crfsSubOrder[keyA] ?? 99) - (crfsSubOrder[keyB] ?? 99);
        }
        return coreA - coreB;
    }).forEach(data => {
        const option = document.createElement("option");
        option.value = data.standardId;
        option.textContent = data.friendlyName?.toUpperCase();
        if (data.standardId === CSLstandardID) {
            option.selected = true;
        }
        friendlyFilterSelect.appendChild(option);
    });
	
	// Trigger re-fetch on dropdown change
	friendlyFilterSelect.addEventListener('change', async function () {
		const selectedCSLStandardID = this.value;
		const selectedText = this.options[this.selectedIndex].text;
		await openScorecardDetail(selectedCSLStandardID, selectedText);
	});

	setScorecardDetailLoaderVisible(false);

	const modalEl = getScorecardDetailModal();
	if (modalEl && !modalEl.dataset.scorecardResizeBound) {
		modalEl.dataset.scorecardResizeBound = '1';
		modalEl.addEventListener('shown.bs.modal', () => {
			requestAnimationFrame(() => {
				if (charts.chartCanvas && typeof charts.chartCanvas.resize === 'function') {
					charts.chartCanvas.resize();
				}
			});
		});
	}
}

// Function to group and count by mapping prefix
function groupCRFandCountByMapping(CRFsafeguards) {
    const prefixCount = {}; // Object to store the count for each prefix
    console.log('CRFsafeguards:', CRFsafeguards); // Log the input array

    CRFsafeguards.forEach(safeguard => {
        console.log('Processing safeguard:', safeguard); // Log each safeguard being processed

        // Extract the prefix (part before the hyphen)
        const prefix = safeguard.split('-')[0];
        console.log('Extracted prefix:', prefix); // Log the extracted prefix

        // Increment the count for the prefix
        if (prefixCount[prefix]) {
            prefixCount[prefix]++;
        } else {
            prefixCount[prefix] = 1;
        }
        console.log('Updated prefixCount:', prefixCount); // Log the updated prefixCount object
    });

    console.log('Final prefixCount:', prefixCount); // Log the final prefixCount object
    return prefixCount;
}

// Function to extract unique CRF safeguard IDs
function extractUniqueCRFSafeguards(year) {
    const safeguardsSet = new Set(); // Set to store unique safeguard IDs

    if (dataCache.CRF[year] && dataCache.CRF[year].Items) {
        dataCache.CRF[year].Items.forEach(item => {
            const safeguardId = item.safeguardid?.S;
            if (safeguardId) {
                safeguardsSet.add(safeguardId); // Add to the set to ensure uniqueness
            }
        });
    }

    // Convert the set to an array for easier manipulation
    const CRFsafeguards = Array.from(safeguardsSet);

    console.log('Unique CRF safeguard IDs:', CRFsafeguards); // Log the unique safeguard IDs

    // Call the grouping function and log the result
    const groupedCRFcounts = groupCRFandCountByMapping(CRFsafeguards);
	console.log('Grouped CRF counts by prefix:', groupedCRFcounts);

    return CRFsafeguards; // Return the unique safeguard IDs as an array
}


// Function to filter and process policy data for chart generation
function buildStandardControlsFromCSData(parsedDataCS, currentStandardControls, uniqueControlsSet) {
    if (!parsedDataCS || !Array.isArray(parsedDataCS.Items)) {
        return;
    }

    parsedDataCS.Items.forEach(item => {
        const mappingsWithId = item.crfmapping.S.split('\n').map(str => ({
            mapping: str.trim(),
            friendlyName: item.friendlyname.S
        }));

        mappingsWithId.forEach(mappingObj => {
            const uniqueKey = `${mappingObj.mapping}-${mappingObj.friendlyName}`;
            if (!uniqueControlsSet.has(uniqueKey)) {
                uniqueControlsSet.add(uniqueKey);
                currentStandardControls.push({
                    mapping: mappingObj.mapping,
                    friendlyName: mappingObj.friendlyName
                });
            }
        });
    });
}

async function filterStandardsData(CSLstandardIDsParam) {
    CSLstandardIDsParam = Array.isArray(CSLstandardIDsParam) ? CSLstandardIDsParam : [CSLstandardIDsParam];
    const effectiveIds = CSLstandardIDsParam.filter(id => String(id).toUpperCase() !== "ALL");

    if (effectiveIds.length === 1) {
        const cacheKey = getStandardCacheKey(effectiveIds[0]);
        if (scorecardResultsCache[cacheKey]) {
            groupedStandardCounts = scorecardResultsCache[cacheKey];
            updateScorecardTable(groupedStandardCounts, groupedCRFcounts);
            return;
        }
    }

    let currentStandardControls = [];
    let uniqueControlsSet = new Set();

    for (let CSLstandardID of effectiveIds) {
        const cacheKey = getStandardCacheKey(CSLstandardID);
        console.log('Processing CSLstandardID:', CSLstandardID);

        let parsedDataCS = dataCache.CS[cacheKey];
        if (!parsedDataCS) {
            try {
                const ajaxData = {
                    action: 'api_action',
                    CSLstandardID: CSLstandardID,
                    apis: ['CS']
                };

                console.log('Sending AJAX request with data:', ajaxData);

                const response = await jQuery.ajax({
                    type: 'POST',
                    url: myAjax.ajaxurl,
                    data: ajaxData,
                    dataType: 'json'
                });

                console.log('API response for CSLstandardID:', CSLstandardID, response);

                parsedDataCS = response.CS || {};
                dataCache.CS[cacheKey] = parsedDataCS;
            } catch (error) {
                console.error("Error fetching data from API for CSLstandardID:", CSLstandardID, error);
                throw error;
            }
        } else {
            console.log('Using cached CS data for CSLstandardID:', CSLstandardID);
        }

        buildStandardControlsFromCSData(parsedDataCS, currentStandardControls, uniqueControlsSet);
    }

    console.log('All unique controls for selected standard:', currentStandardControls);

    groupedStandardCounts = groupStandardsAndCountByMapping(currentStandardControls);
    console.log('All grouped controls for current standard selection:', groupedStandardCounts);

    if (effectiveIds.length === 1) {
        scorecardResultsCache[getStandardCacheKey(effectiveIds[0])] = groupedStandardCounts;
    }

    updateScorecardTable(groupedStandardCounts, groupedCRFcounts);
}

function updateScorecardTable(groupedStandardCounts, groupedCRFcounts) {
    //console.log('updateScorecardTable init');
    
    // Log the input data for debugging
    console.log('Grouped Standard Counts:', groupedStandardCounts);
    console.log('Grouped CRF Counts:', groupedCRFcounts);

    // Loop through each key in the groupedCRFcounts (CRF data)
    Object.keys(groupedCRFcounts).forEach(prefix => {
        // Log the current prefix being processed
        console.log(`Processing prefix: ${prefix}`);
        
        // Get the standard table value for this prefix
        const standardValue = groupedStandardCounts[prefix] || 0; // Default to 0 if not found
        const crfValue = groupedCRFcounts[prefix] || 0; // Default to 0 if not found
        
        // Log the values for the current prefix
        console.log(`Standard value for ${prefix}:`, standardValue);
        console.log(`CRF value for ${prefix}:`, crfValue);
        
        // Calculate the result (standard value divided by CRF value), handle division by zero
        const result = crfValue !== 0 ? (standardValue / crfValue).toFixed(2) : 'N/A';
        
        // Log the result of the calculation
        console.log(`Result for ${prefix}:`, result);

        // Select the cell with the corresponding class name in the new table (e.g., crf-pm)
        const scorecardCell = document.querySelector(`.crf-${prefix.toLowerCase()}`);
        
        // Log whether the cell was found
        if (scorecardCell) {
            //console.log(`Updating scorecard cell for prefix ${prefix}`);
            scorecardCell.textContent = result;
        } else {
            console.warn(`No scorecard cell found for prefix ${prefix}`);
        }
    });

    // Calculate the total value for both groupedStandardCounts and groupedCRFcounts
    const totalStandardValue = Object.values(groupedStandardCounts).reduce((sum, val) => sum + val, 0);
    const totalCrfValue = Object.values(groupedCRFcounts).reduce((sum, val) => sum + val, 0);

    // Log the total values for both groupedStandardCounts and groupedCRFcounts
    console.log('Total Standard Value:', totalStandardValue);
    console.log('Total CRF Value:', totalCrfValue);

    // Calculate overall result, handling division by zero
    const overallResult = totalCrfValue !== 0 ? (totalStandardValue / totalCrfValue).toFixed(2) : 'N/A';

    // Log the overall result
    console.log('Overall Result:', overallResult);

    // Update the overall result in the scorecard table
    const overallCell = document.querySelector('.crf-overall');
    if (overallCell) {
        //console.log('Updating overall scorecard cell');
        overallCell.textContent = overallResult;
    } else {
        console.warn('No overall scorecard cell found');
    }
	
    ensureScorecardDetailTablesVisible();
    calculateAveragesAndUpdateTable();
    createChartFromTable('scorecardTable', 'chartCanvas', charts, true);

    console.log('updateScorecardTable ran');

    const friendlyFilter = document.getElementById('friendlyname-filter');
    if (friendlyFilter && friendlyFilter.value && friendlyFilter.value !== 'all') {
        const selectedText = friendlyFilter.options[friendlyFilter.selectedIndex]?.text || '';
        const payload = buildScorecardPayloadFromCurrentState(friendlyFilter.value, selectedText);
        persistScorecardPayload(payload);
    }
}


// Function to group and count by mapping prefix
function groupStandardsAndCountByMapping(currentStandardControls) {
	const prefixCount = {}; // Object to store the count for each prefix

	currentStandardControls.forEach(control => {
		// Extract the prefix (part before the hyphen)
		const prefix = control.mapping.split('-')[0];

		// Increment the count for the prefix
		if (prefixCount[prefix]) {
			prefixCount[prefix]++;
		} else {
			prefixCount[prefix] = 1;
		}
	});
	return prefixCount;
}




// Initialize on page load
fetchData("2026", ['all'], true);
